import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { apiPaymentStatus } from "../lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const { t } = useLang();
  const { reload } = useAuth();
  const [params] = useSearchParams();
  const [state, setState] = useState("processing");

  const poll = useCallback(async (sessionId, tries) => {
    if (tries <= 0) { setState("processing"); return; }
    try {
      const d = await apiPaymentStatus(sessionId);
      if (d.payment_status === "paid") { await reload(); setState("paid"); return; }
    } catch (_) { /* keep polling */ }
    setTimeout(() => poll(sessionId, tries - 1), 2000);
  }, [reload]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) poll(sid, 8); else setState("paid");
  }, [params, poll]);

  return (
    <div className="tw-grid-bg flex min-h-screen items-center justify-center px-4 pt-28 pb-16" data-testid="payment-success-page">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center">
        {state === "processing" ? (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-blue-700" />
            <p className="mt-4 text-slate-600">{t("account.payProcessing")}</p>
          </>
        ) : (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
            <h1 className="mt-4 text-2xl font-bold text-slate-900">{t("account.paySuccessTitle")}</h1>
            <p className="mt-2 text-slate-500">{t("account.paySuccessDesc")}</p>
            <Link to="/account" data-testid="pay-to-account" className="mt-6 inline-block rounded-full bg-[#1E3A8A] px-6 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8]">
              {t("account.backToAccount")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  return (
    <div className="tw-grid-bg flex min-h-screen items-center justify-center px-4 pt-28 pb-16" data-testid="payment-cancel-page">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center">
        <XCircle className="mx-auto h-12 w-12 text-slate-500" />
        <h1 className="mt-4 text-2xl font-bold text-slate-900">{t("account.payCancelTitle")}</h1>
        <p className="mt-2 text-slate-500">{t("account.payCancelDesc")}</p>
        <Link to="/pricing" className="mt-6 inline-block rounded-full bg-[#1E3A8A] px-6 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8]">
          {t("account.viewPricing")}
        </Link>
      </div>
    </div>
  );
}
